// ===========================================
// School Directory Card (public /schools page)
// ===========================================
// Extracted from SchoolsDirectoryPage on 2026-09-07 so the card can be tested.
// Adds the main location's map image ABOVE the address link (Andrew D3), built
// server-side (`mainLocation.mapImageUrl`); nothing when it is null (D4). No
// suburb line — the full address is already on the card (D1).

import { useState } from 'react';
import { Box, Button, Card, CardActions, CardContent, CardMedia, Typography } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LanguageIcon from '@mui/icons-material/Language';
import PhoneIcon from '@mui/icons-material/Phone';
import { Link } from 'react-router-dom';

export interface DirectorySchool {
  slug: string;
  name: string;
  description?: string;
  email?: string;
  phone?: string;
  websiteUrl?: string;
  branding: {
    logoUrl?: string;
    primaryColor?: string;
  };
  mainLocation?: {
    name: string;
    address?: string | null;
    phone?: string | null;
    latitude?: number | null;
    longitude?: number | null;
    /** Server-built Mapbox static image; null when the location has no coordinates. */
    mapImageUrl?: string | null;
  } | null;
}

export interface SchoolDirectoryCardProps {
  school: DirectorySchool;
  onEnquire: (school: DirectorySchool) => void;
}

const linkSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  mb: 0.5,
  textDecoration: 'none',
  color: 'text.primary',
  '&:hover': { color: 'primary.main' },
} as const;

export default function SchoolDirectoryCard({ school, onEnquire }: SchoolDirectoryCardProps) {
  // A map that fails to load (token revoked, Mapbox down) must not leave a broken
  // image on a public page — drop back to the address link only.
  const [mapFailed, setMapFailed] = useState(false);
  const address = school.mainLocation?.address || null;
  const mapsHref = address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
    : null;
  const mapImageUrl = !mapFailed ? school.mainLocation?.mapImageUrl || null : null;

  return (
    <Card
      data-testid={`school-card-${school.slug}`}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'rgba(255, 255, 255, 0.8)',
        border: '2px solid rgba(69, 128, 228, 0.1)',
        transition: 'all 0.3s ease',
        '&:hover': {
          border: '2px solid primary.main',
          boxShadow: '0 4px 12px rgba(69, 128, 228, 0.15)',
        },
      }}
    >
      {school.branding.logoUrl && (
        <CardMedia
          component="img"
          image={school.branding.logoUrl}
          alt={`${school.name} logo`}
          sx={{ width: '100%', height: 180, objectFit: 'contain', p: 3, bgcolor: 'rgba(69, 128, 228, 0.05)' }}
        />
      )}
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          {school.name}
        </Typography>
        {school.description && (
          <Typography variant="body2" color="text.secondary" paragraph>
            {school.description}
          </Typography>
        )}

        {/* Map of the main location, above the address it belongs to (D3). */}
        {mapImageUrl && (
          <Box
            component={mapsHref ? 'a' : 'div'}
            href={mapsHref ?? undefined}
            target={mapsHref ? '_blank' : undefined}
            rel={mapsHref ? 'noopener noreferrer' : undefined}
            data-testid={`school-map-${school.slug}`}
            sx={{ display: 'block', mb: 1, borderRadius: 1, overflow: 'hidden', lineHeight: 0 }}
          >
            <Box
              component="img"
              src={mapImageUrl}
              alt={`Map of ${school.mainLocation?.name ?? school.name}`}
              loading="lazy"
              onError={() => setMapFailed(true)}
              sx={{ width: '100%', height: 160, objectFit: 'cover', display: 'block' }}
            />
          </Box>
        )}

        {address && mapsHref && (
          <Box component="a" href={mapsHref} target="_blank" rel="noopener noreferrer" sx={linkSx}>
            <LocationOnIcon fontSize="small" color="action" />
            <Typography variant="body2">{address}</Typography>
          </Box>
        )}
        {school.websiteUrl && (
          <Box component="a" href={school.websiteUrl} target="_blank" rel="noopener noreferrer" sx={linkSx}>
            <LanguageIcon fontSize="small" color="action" />
            <Typography variant="body2">{school.websiteUrl.replace(/^https?:\/\//, '')}</Typography>
          </Box>
        )}
        {school.phone && (
          <Box component="a" href={`tel:${school.phone.replace(/\s/g, '')}`} sx={{ ...linkSx, mb: 0 }}>
            <PhoneIcon fontSize="small" color="action" />
            <Typography variant="body2">{school.phone}</Typography>
          </Box>
        )}
      </CardContent>
      <CardActions sx={{ p: 2, pt: 0, flexWrap: 'wrap', gap: 1 }}>
        <Button component={Link} to={`/${school.slug}`} variant="text" color="primary" fullWidth>
          Visit Profile
        </Button>
        <Button component={Link} to={`/${school.slug}/login`} variant="outlined" fullWidth>
          Login
        </Button>
        <Button variant="contained" color="primary" fullWidth onClick={() => onEnquire(school)}>
          Make Enquiry
        </Button>
      </CardActions>
    </Card>
  );
}
